import { useQuery } from '@tanstack/react-query';
import { type ReactElement } from 'react';

import { fetchCohorts, fetchCourses } from '@/features/masters/api';
import type { Role } from '@/features/auth/api';

export type PeopleRoleFilter = '' | 'student' | 'director' | 'success';

export interface MessagePeopleFilterValues {
  role: PeopleRoleFilter;
  cohortId: string;
  courseId: string;
  q: string;
}

export const EMPTY_PEOPLE_FILTERS: MessagePeopleFilterValues = {
  role: '',
  cohortId: '',
  courseId: '',
  q: '',
};

const ROLE_OPTIONS: { value: PeopleRoleFilter; label: string }[] = [
  { value: '', label: 'すべて' },
  { value: 'student', label: '受講生' },
  { value: 'director', label: '理事・講師' },
  { value: 'success', label: 'サクセス担当' },
];

/** 問い合わせ・連絡先の差出人／相手をロール・期・コース・氏名で絞る（D-356） */
export function MessagePeopleFilters({
  values,
  onChange,
  onSearch,
}: {
  values: MessagePeopleFilterValues;
  onChange: (next: MessagePeopleFilterValues) => void;
  onSearch: () => void;
}): ReactElement {
  const cohorts = useQuery({ queryKey: ['cohorts'], queryFn: fetchCohorts });
  const courses = useQuery({ queryKey: ['courses'], queryFn: fetchCourses });
  const showMemberFilters = values.role === '' || values.role === 'student';

  function setRole(role: PeopleRoleFilter): void {
    onChange({
      ...values,
      role,
      cohortId: role === 'director' || role === 'success' ? '' : values.cohortId,
      courseId: role === 'director' || role === 'success' ? '' : values.courseId,
    });
  }

  return (
    <div className="mb-4 flex flex-wrap items-end gap-3 rounded-sm border border-wpborder bg-white px-4 py-3">
      <label className="block min-w-[8rem]">
        <span className="mb-1.5 block text-wptext-muted">ロール</span>
        <select
          value={values.role}
          onChange={(e) => setRole(e.target.value as PeopleRoleFilter)}
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        >
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      {showMemberFilters && (
        <>
          <label className="block min-w-[8rem]">
            <span className="mb-1.5 block text-wptext-muted">期</span>
            <select
              value={values.cohortId}
              onChange={(e) => onChange({ ...values, cohortId: e.target.value })}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="">すべて</option>
              {(cohorts.data ?? [])
                .filter((c) => c.is_active || c.id === values.cohortId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="block min-w-[8rem]">
            <span className="mb-1.5 block text-wptext-muted">コース</span>
            <select
              value={values.courseId}
              onChange={(e) => onChange({ ...values, courseId: e.target.value })}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="">すべて</option>
              {(courses.data ?? [])
                .filter((c) => c.is_active || c.id === values.courseId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
        </>
      )}
      <label className="block min-w-[12rem] flex-1">
        <span className="mb-1.5 block text-wptext-muted">氏名・メール</span>
        <input
          type="search"
          value={values.q}
          onChange={(e) => onChange({ ...values, q: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSearch();
          }}
          placeholder="名前の一部で検索"
          className="w-full rounded-sm border border-wpborder px-3 py-2"
        />
      </label>
      <button type="button" onClick={onSearch} className="btn-secondary">
        絞り込む
      </button>
    </div>
  );
}

export function peopleFiltersToQuery(values: MessagePeopleFilterValues): {
  role?: Role;
  cohort_id?: string;
  course_id?: string;
  q?: string;
} {
  const query: {
    role?: Role;
    cohort_id?: string;
    course_id?: string;
    q?: string;
  } = {};
  if (values.role) query.role = values.role;
  if (values.cohortId) query.cohort_id = values.cohortId;
  if (values.courseId) query.course_id = values.courseId;
  if (values.q.trim()) query.q = values.q.trim();
  return query;
}

export function roleLabel(role: Role | null | undefined): string {
  if (role === 'student') return '受講生';
  if (role === 'director') return '理事・講師';
  if (role === 'success') return 'サクセス担当';
  return '—';
}
